// Services.jsx  —  Solutions page
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import {
  ShieldCheck, GraduationCap, Eye, FolderTree,
  Users, Briefcase, Building2, ArrowRight, ChevronRight,
  Check, Lock, FileCheck, Award, Layers
} from 'lucide-react';
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import './Services.css';

const site = 'https://your-platform.com';

function FadeUp({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function ServicesPage() {
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);

  const content = {
    // ── S-01 — Hero ──
    hero: {
      eyebrow: 'SOLUTIONS',
      headline: 'Solutions for Competency, Development and Project Assurance.',
      subheadline:
        'Four outcomes — verified competency, structured mentorship, controlled professional access and auditable project collaboration — designed for professionals, mentors and organisations operating in assured industries.',
    },

    // ── S-02 + S-06 + S-08 + S-09 — Four public solution sections ──
    solutions: [
      {
        // S-02 — Competency Assurance (public: labels + principles only)
        id: 'competency',
        eyebrow: 'SOLUTION 01',
        title: 'Competency Assurance',
        purpose:
          'A structured, evidence-based framework that makes technical competency comparable across roles, organisations and regions — without exposing assessment rules or thresholds.',
        principles: [
          'Evidence-led — every progression step is supported by documented field evidence and mentor validation.',
          'Industry-aligned — calibrated to recognised international inspection disciplines and qualification schemes.',
          'Portable and verifiable — competency records travel with the professional and can be verified by authorised parties.',
        ],
        progressionLabel: 'Broad progression pathway',
        progression: [
          'Aspirant',
          'Trainee',
          'Junior',
          'Independent',
          'Senior',
          'Principal',
        ],
        progressionNote:
          'Progression is not automatic. Level eligibility, evidence rules, scoring and approvals are reviewed inside the authenticated framework.',
        icon: 'ShieldCheck',
      },
      {
        // S-06 — Mentorship & Development (single consolidated section)
        id: 'mentorship',
        eyebrow: 'SOLUTION 02',
        title: 'Mentorship & Development',
        purpose:
          'Approved mentors support assigned professionals through development plans, structured evidence review, and documented recommendations — inside a single governed workflow.',
        principles: [
          'Assigned relationships — mentors and professionals are matched through an approved assignment process.',
          'Structured development — evidence, feedback and recommendations are captured in one continuous record.',
          'Documented outcomes — every recommendation is attributable, traceable and reviewable.',
        ],
        note:
          'Matching logic, mentor dashboards, review workflows and candidate progression rules are available inside the authenticated platform.',
        icon: 'GraduationCap',
      },
      {
        // S-08 — Verified Professional Access (no directory, no open search)
        id: 'access',
        eyebrow: 'SOLUTION 03',
        title: 'Verified Professional Access',
        purpose:
          'Organisations submit requirements through a controlled workflow. Verified professionals are surfaced only through masked shortlists, and detailed records are disclosed only with the professional\'s consent.',
        principles: [
          'Controlled requirement submission — organisations describe the requirement; the platform curates the response.',
          'Masked shortlists — identifying details are withheld until mutual consent is in place.',
          'Consent-based disclosure — professional records become visible to an organisation only after explicit approval.',
        ],
        note:
          'There is no public professional directory or unrestricted search endpoint.',
        icon: 'Eye',
      },
      {
        // S-09 — Project Collaboration & Records (single outcome, no module detail)
        id: 'projects',
        eyebrow: 'SOLUTION 04',
        title: 'Project Collaboration & Records',
        purpose:
          'Approved project users collaborate inside controlled workspaces where authorised records are shared and every significant action is auditable.',
        principles: [
          'Controlled workspaces — access is scoped to the project, the role and the entitlement.',
          'Authorised records — parties see only the records their role permits.',
          'Auditable actions — significant events are timestamped and independently reviewable.',
        ],
        note:
          'Detailed module functionality is available inside approved dashboard access and enterprise documentation.',
        icon: 'FolderTree',
      },
    ],

    // ── S-02 related — Competency section disclaimer (also serves S-04) ──
    competencyDisclaimer: {
      title: 'What competency records may include',
      body:
        'Qualifications, certifications and project evidence may be considered as part of an authenticated competency record. The platform does not imply endorsement, equivalency or automatic recognition of any external certification.',
    },

    // ── S-05 — Competency Passport (illustration only, no sample data) ──
    passport: {
      title: 'A portable competency record',
      body:
        'Approved users maintain a single, portable competency record that travels with them across employers, projects and jurisdictions — and is disclosed only where they choose to share it.',
      points: [
        'Built from verified evidence, not self-declared claims.',
        'Visible only under consent-based sharing controls.',
        'Audit-ready for clients, employers and regulators.',
      ],
    },

    // ── S-11 — Cross-industry capability (not an industry) ──
    crossIndustry: {
      title: 'Cross-Industry Quality, Inspection & Compliance',
      body:
        'Quality, inspection and compliance are cross-industry capabilities supported by the platform — not a market sector. They apply wherever verified technical capability and auditable project records matter.',
    },

    // ── S-07 + S-12 — Single controlled CTA ──
    cta: {
      headline: 'Choose your path',
      subheadline:
        'Every path enters the same controlled identity and verification service. Approval is not automatic.',
      primary: 'Join Ecosystem',
      secondary: 'Sign In',
    },
  };

  const solutionIconMap = { ShieldCheck, GraduationCap, Eye, FolderTree };

  const handleJoin = () => setIsRoleModalOpen(true);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${site}/services#webpage`,
    name: 'Solutions — Competency, Development and Project Assurance',
    url: `${site}/services`,
    isPartOf: { '@id': `${site}/#website` },
    about: { '@id': `${site}/#organization` },
  };

  return (
    <>
      <Helmet>
        <title>Solutions — Competency, Development and Project Assurance</title>
        <meta
          name="description"
          content="Four solution areas for assured industries: competency assurance, mentorship and development, verified professional access, and auditable project collaboration."
        />
        <link rel="canonical" href={`${site}/services`} />
        <meta property="og:title" content="Solutions — Competency, Development and Project Assurance" />
        <meta property="og:description" content="Verified competency, structured development, controlled access and auditable assurance." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${site}/services`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main>
        {/* ── HERO (S-01) ── */}
        <section className="solutions-hero">
          <div className="solutions-hero-top-line" />
          <div className="solutions-hero-grid-overlay" />
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              className="solutions-hero-content"
            >
              <div className="solutions-hero-eyebrow">
                <div className="solutions-hero-line" />
                <span>{content.hero.eyebrow}</span>
              </div>
              <h1 className="solutions-hero-title">{content.hero.headline}</h1>
              <p className="solutions-hero-subtitle">{content.hero.subheadline}</p>

              <div className="solutions-hero-actions">
                <button
                  type="button"
                  className="solutions-cta-primary"
                  onClick={handleJoin}
                >
                  Join Ecosystem <ArrowRight size={16} />
                </button>
                <a href="/login" className="solutions-cta-secondary">
                  Sign In
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── FOUR SOLUTION SECTIONS (S-02, S-06, S-08, S-09) ── */}
        {content.solutions.map((sol, index) => {
          const Icon = solutionIconMap[sol.icon] || ShieldCheck;
          const isAlt = index % 2 === 1;
          return (
            <section
              key={sol.id}
              className={`solutions-block ${isAlt ? 'solutions-block-alt' : ''}`}
            >
              <div className="container">
                <div className="solutions-block-grid">
                  {/* Content */}
                  <FadeUp className="solutions-block-content">
                    <div className="solutions-block-eyebrow">
                      <div className="solutions-hero-line" />
                      <span>{sol.eyebrow}</span>
                    </div>
                    <h2 className="solutions-block-title">{sol.title}</h2>
                    <p className="solutions-block-purpose">{sol.purpose}</p>

                    <ul className="solutions-principles">
                      {sol.principles.map((p) => (
                        <li key={p}>
                          <div className="solutions-principle-check">
                            <Check size={12} />
                          </div>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Progression strip is only for Competency Assurance */}
                    {sol.progression && (
                      <div className="solutions-progression">
                        <div className="solutions-progression-label">
                          {sol.progressionLabel}
                        </div>
                        <div className="solutions-progression-track">
                          {sol.progression.map((label, i) => (
                            <div key={label} className="solutions-progression-step">
                              <span className="solutions-progression-dot" />
                              <span className="solutions-progression-name">
                                {label}
                              </span>
                              {i < sol.progression.length - 1 && (
                                <ChevronRight
                                  size={14}
                                  className="solutions-progression-arrow"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                        <p className="solutions-progression-note">
                          {sol.progressionNote}
                        </p>
                      </div>
                    )}

                    {sol.note && (
                      <p className="solutions-block-note">
                        <Lock size={14} className="solutions-block-note-icon" />
                        {sol.note}
                      </p>
                    )}
                  </FadeUp>

                  {/* Visual panel */}
                  <FadeUp delay={0.1} className="solutions-block-visual">
                    <div className="solutions-block-icon-panel">
                      <div className="solutions-block-icon">
                        <Icon size={26} />
                      </div>
                      <div className="solutions-block-panel-label">
                        {sol.title}
                      </div>
                      <div className="solutions-block-panel-rule" />
                      <div className="solutions-block-panel-caption">
                        Detailed capabilities are reviewed inside approved
                        access.
                      </div>
                    </div>
                  </FadeUp>
                </div>
              </div>
            </section>
          );
        })}

        {/* ── COMPETENCY RECORD DISCLAIMER (S-02 + S-04) ── */}
        <section className="solutions-disclaimer-section">
          <div className="container">
            <FadeUp>
              <div className="solutions-disclaimer-card">
                <div className="solutions-disclaimer-icon">
                  <Award size={20} />
                </div>
                <div>
                  <h3 className="solutions-disclaimer-title">
                    {content.competencyDisclaimer.title}
                  </h3>
                  <p className="solutions-disclaimer-body">
                    {content.competencyDisclaimer.body}
                  </p>
                </div>
              </div>
            </FadeUp>
          </div>
        </section>

        {/* ── COMPETENCY RECORD (S-05) — illustration only, no sample data ── */}
        <section className="solutions-passport-section">
          <div className="container">
            <div className="solutions-passport-grid">
              <FadeUp>
                <div className="solutions-passport-content">
                  <div className="solutions-block-eyebrow">
                    <div className="solutions-hero-line" />
                    <span>PORTABLE RECORD</span>
                  </div>
                  <h2 className="solutions-passport-title">
                    {content.passport.title}
                  </h2>
                  <p className="solutions-passport-body">
                    {content.passport.body}
                  </p>
                  <ul className="solutions-passport-points">
                    {content.passport.points.map((p) => (
                      <li key={p}>
                        <div className="solutions-principle-check">
                          <Check size={12} />
                        </div>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeUp>

              {/* Abstract illustration — deliberately no identity, no metrics, no certification tags */}
              <FadeUp delay={0.12}>
                <div
                  className="solutions-passport-illustration"
                  aria-hidden="true"
                >
                  <div className="solutions-passport-illustration-inner">
                    <div className="solutions-passport-chip">
                      <ShieldCheck size={20} />
                      <span>Verified</span>
                    </div>
                    <div className="solutions-passport-lines">
                      <span className="solutions-passport-line solutions-passport-line-w-70" />
                      <span className="solutions-passport-line solutions-passport-line-w-45" />
                      <span className="solutions-passport-line solutions-passport-line-w-60" />
                    </div>
                    <div className="solutions-passport-blocks">
                      <span className="solutions-passport-block" />
                      <span className="solutions-passport-block" />
                      <span className="solutions-passport-block" />
                    </div>
                    <div className="solutions-passport-lock">
                      <Lock size={12} />
                      <span>Consent-controlled</span>
                    </div>
                  </div>
                </div>
              </FadeUp>
            </div>
          </div>
        </section>

        {/* ── CROSS-INDUSTRY CAPABILITY (S-11) ── */}
        <section className="solutions-cross-industry-section">
          <div className="container">
            <FadeUp className="solutions-cross-industry-content">
              <div className="solutions-block-eyebrow solutions-block-eyebrow-center">
                <div className="solutions-hero-line" />
                <span>CROSS-INDUSTRY CAPABILITY</span>
                <div className="solutions-hero-line" />
              </div>
              <h2>{content.crossIndustry.title}</h2>
              <p>{content.crossIndustry.body}</p>

              <div className="solutions-cross-industry-chips">
                <span className="solutions-cross-chip">
                  <FileCheck size={14} /> Quality
                </span>
                <span className="solutions-cross-chip">
                  <Eye size={14} /> Inspection
                </span>
                <span className="solutions-cross-chip">
                  <ShieldCheck size={14} /> Compliance
                </span>
              </div>

              <a href="/industries" className="solutions-cross-industry-cta">
                Explore industries <ArrowRight size={16} />
              </a>
            </FadeUp>
          </div>
        </section>

        {/* ── CTA (S-07 + S-12) ── */}
        <section className="solutions-cta-section">
          <div className="solutions-cta-top-line" />
          <div className="container">
            <FadeUp>
              <div className="solutions-cta-content">
                <h2>{content.cta.headline}</h2>
                <p>{content.cta.subheadline}</p>
                <div className="solutions-cta-buttons">
                  <button
                    type="button"
                    className="solutions-cta-primary"
                    onClick={handleJoin}
                  >
                    {content.cta.primary} <ArrowRight size={16} />
                  </button>
                  <a href="/login" className="solutions-cta-secondary">
                    {content.cta.secondary}
                  </a>
                </div>
              </div>
            </FadeUp>
          </div>
        </section>
      </main>

      {/* Role selector — same modal pattern used across the site (S-07, S-12) */}
      {isRoleModalOpen && <RoleModal onClose={() => setIsRoleModalOpen(false)} />}

      <Footer />
    </>
  );
}

/* ── Role selector modal ──
   "Technical Professional" is the public label; the internal slug
   remains "candidate". Mentor path is "Join as Mentor" — it enters
   the same identity/verification service as every other path.
*/
function RoleModal({ onClose }) {
  const options = [
    { slug: 'candidate',    label: 'Technical Professional', description: 'Build a verified competency record.', icon: Briefcase },
    { slug: 'mentor',       label: 'Mentor',                 description: 'Support assigned professionals.',     icon: Users },
    { slug: 'organization', label: 'Organisation',           description: 'Request organisation access.',         icon: Building2 },
  ];

  const select = (slug) => {
    window.location.href = `/join-ecosystem?role=${slug}`;
  };

  return (
    <div
      className="role-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="role-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="role-modal-title"
      >
        <div className="role-modal-header">
          <div>
            <h2 id="role-modal-title" className="role-modal-title">
              Join the Ecosystem
            </h2>
            <p className="role-modal-subtitle">
              Choose how you'd like to get started.
            </p>
          </div>
          <button
            type="button"
            className="role-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="role-modal-options">
          {options.map((opt) => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.slug}
                type="button"
                className="role-option"
                onClick={() => select(opt.slug)}
              >
                <span className="role-option-icon">
                  <Icon size={20} />
                </span>
                <span className="role-option-body">
                  <span className="role-option-label">{opt.label}</span>
                  <span className="role-option-desc">{opt.description}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}